import { Dropdown, Select, Skeleton, Spin } from 'antd';
import Search from 'antd/lib/input/Search';
import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Column } from 'react-base-table';
import type { ColumnShape } from 'react-base-table';
import { useSelector } from 'react-redux';

import Icon, { LoadingOutlined, SearchOutlined } from '@ant-design/icons';
import {
  useGetBlockEventsName,
  useGetMappedStatusEvent,
  usePostDimensionData,
} from '@api/emp/queryHooks';
import ResponsiveStatusTag from '@components/common/DimensionTable/components/ResponsiveStatusTag';
import Sorter from '@components/common/VirtualizedTable/Sorter';
import { useQueryDimensionData } from '@components/mapping-panel/api';
import BaseTable from '@components/quick-map/components/Table/BaseTable';
import { Checkbox } from '@components/quick-map/components/styled';
import useScreenSize from '@hooks/useScreenSize';
import { disshowDropdownDimensionType, mappedEventDimensionType } from '@src/common/constant/enum';
import FilterIcon from '@src/icons/emp/filter-icon.svg';
import commonSelector from '@store/common/selectors';
import { humanize } from '@utils';

import styles from './index.less';

enum ETableFilter {
  ALL,
  Unmapped,
  Mapped,
}

enum ESorterOrder {
  NORMAL = 'normal',
  DESC = 'desc',
  ASC = 'asc',
}

enum ESorterType {
  NAME,
  EVENT,
}

interface ISorterOrderWithType {
  sorterOrder: ESorterOrder;
  type: ESorterType;
}

interface IRowData {
  dimension: string;
  name: string;
  value: number;
}

interface IDimensionTableProps {
  type: disshowDropdownDimensionType;
  selectedList?: string[];
  onSelectedChange?: (target: string, selectedList: string[]) => void;
  onActiveRowChange?: (rowData: IRowData | null) => void;
  onRowClick?: ({ dimension, name, value }: IRowData) => void;
  filters?: any;
}

const NUMBER_KEY = 'app_total_user_events';

export const MAXIMUM_EVENTS_LIMIT = 50;

const Loading = <LoadingOutlined style={{ color: '#949494', fontSize: '11px' }} spin />;

const DimensionTable: React.FC<IDimensionTableProps> = (props) => {
  const { type = disshowDropdownDimensionType.mapped_event } = props;

  const [activeFilter, setActiveFilter] = useState<ETableFilter | null>(ETableFilter.ALL);
  const [activeRow, setActiveRow] = useState<IRowData | null>(null);
  const [dimensionKey, setDimensionKey] = useState<mappedEventDimensionType>(
    mappedEventDimensionType.all_event_name,
  );
  const [dimensionData, setDimensionData] = useState<any[]>([]);
  const [tableData, setTableData] = useState<any[]>([]);
  const [selectedList, setSelectedList] = useState<string[]>(props.selectedList || []);
  const [sorterOrder, setSorterOrder] = useState<ISorterOrderWithType>({
    sorterOrder: ESorterOrder.NORMAL,
    type: ESorterType.NAME,
  });
  const [searchText, setSearchText] = useState<string>('');
  const { mutate: getDimensionData, isLoading } = usePostDimensionData();
  const { data: mappedEvents } = useGetMappedStatusEvent('event_name');
  const { data: blockedEventsName } = useGetBlockEventsName();
  const common = useSelector(commonSelector);
  const { screenWidth: currentScreenWidth } = useScreenSize();
  const { data: totalEventsData, isFetching: totalEventsLoading } = useQueryDimensionData({}, 0);

  const eventCount = useMemo(() => {
    const count = totalEventsData?.[0]?.[NUMBER_KEY] ?? 0;
    return humanize(count, '');
  }, [totalEventsData]);

  useEffect(() => {
    setSelectedList(props.selectedList || selectedList);
  }, [props.selectedList]);

  const DimensionTitle = () => {
    if (type === disshowDropdownDimensionType.merged_event_tag_key) {
      return <div className={styles.dimensionTitle}>Event Tag Key</div>;
    } else if (type === disshowDropdownDimensionType.merged_event_tag_value) {
      return <div className={styles.dimensionTitle}>Event Tag Value</div>;
    } else {
      return (
        <div className={styles.dimensionSelect}>
          <Select
            value={dimensionKey}
            options={[
              { label: 'All Event', value: mappedEventDimensionType.all_event_name },
              { label: 'Conviva Event', value: mappedEventDimensionType.conviva_event_name },
              { label: 'Custom Event', value: mappedEventDimensionType.custom_event_name },
            ]}
            onChange={(value) => {
              setDimensionKey(value);
            }}
            style={{ width: 150 }}
            bordered={false}
          />
        </div>
      );
    }
  };

  const handleSorterClick = (type: ESorterType) => {
    setSorterOrder((prev) => {
      if (prev.type !== type) {
        return {
          type,
          sorterOrder: ESorterOrder.DESC,
        };
      }
      let nextOrder;
      switch (prev.sorterOrder) {
        case ESorterOrder.NORMAL:
          nextOrder = ESorterOrder.DESC;
          break;
        case ESorterOrder.DESC:
          nextOrder = ESorterOrder.ASC;
          break;
        case ESorterOrder.ASC:
          nextOrder = ESorterOrder.NORMAL;
          break;
      }
      return {
        type,
        sorterOrder: nextOrder,
      };
    });
  };

  const CustomTableHeader = () => {
    let nameSorterOrder = ESorterOrder.NORMAL;
    let eventSorterOrder = ESorterOrder.NORMAL;
    if (sorterOrder.type === ESorterType.NAME) {
      nameSorterOrder = sorterOrder.sorterOrder;
    } else if (sorterOrder.type === ESorterType.EVENT) {
      eventSorterOrder = sorterOrder.sorterOrder;
    }

    return (
      <div className={styles.tableHeader}>
        <div
          className={styles.sorterContainer}
          onClick={() => {
            handleSorterClick(ESorterType.NAME);
          }}
        >
          <div style={{ marginRight: '4px' }}>Name</div>
          <Sorter sortOrder={nameSorterOrder} />
        </div>
        <div
          className={styles.sorterContainer}
          onClick={() => {
            handleSorterClick(ESorterType.EVENT);
          }}
        >
          <Sorter sortOrder={eventSorterOrder} />
          <div style={{ margin: '0 8px 0 4px' }}>Total Events</div>
          {totalEventsLoading ? <Spin indicator={Loading} /> : <span>{eventCount}</span>}
        </div>
      </div>
    );
  };

  const CustomTableSubHeader = () => {
    const Cells = Object.keys(ETableFilter)
      .filter((v) => isNaN(Number(v)))
      .map((filterName) => {
        return (
          <div
            className={classNames(styles.subHeaderCell, {
              [styles.subHeaderCellActive]: activeFilter === ETableFilter[filterName],
            })}
            onClick={() => {
              setActiveFilter(ETableFilter[filterName]);
            }}
            key={filterName}
          >
            {filterName}
          </div>
        );
      });

    const SearchInput = () => {
      return (
        <Search
          placeholder="Input search text"
          allowClear
          enterButton="Search"
          onSearch={(value) => {
            setSearchText(value);
          }}
          autoFocus
          defaultValue={searchText}
          size="small"
        />
      );
    };

    return (
      <div className={styles.tableSubHeader}>
        <div className={styles.filterContainer}>
          <Icon component={FilterIcon} style={{ fontSize: 14, marginRight: 12 }} />
          {Cells}
        </div>
        <Dropdown
          arrow={false}
          dropdownRender={SearchInput}
          placement="bottomRight"
          trigger={['click']}
        >
          <SearchOutlined style={{ color: '#494949', fontSize: '14px' }} />
        </Dropdown>
      </div>
    );
  };

  const nameCellRenderer: ColumnShape['cellRenderer'] = useCallback(
    ({ cellData }) => {
      if (type === disshowDropdownDimensionType.mapped_event) {
        const checked = selectedList.includes(cellData);
        const isBlocked = (blockedEventsName ?? []).includes(cellData);
        const disabled = isBlocked;
        const showMapped =
          !!(mappedEvents?.data ?? []).find((event) => event?.value === cellData) &&
          activeFilter === ETableFilter.ALL;

        const nameLabel = (
          <div style={{ width: '100%', display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
            <Checkbox
              borderColor={'#0046ff'}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => {
                let newSelectedList = [...selectedList];
                if (e.target.checked) {
                  newSelectedList.length < MAXIMUM_EVENTS_LIMIT && newSelectedList.push(cellData);
                } else {
                  newSelectedList = selectedList.filter((i) => i !== cellData);
                }
                props.onSelectedChange?.(cellData, newSelectedList);
                setSelectedList(newSelectedList);
              }}
              checked={checked}
              disabled={disabled}
              style={{ marginRight: 6 }}
            />
            <span
              className={classNames(styles.nameCellText, {
                [styles.nameCellCheckedText]: checked,
                [styles.disabledText]: isBlocked,
              })}
            >
              {cellData}
            </span>
          </div>
        );

        return (
          <div style={{ width: '100%', display: 'flex', alignItems: 'center' }}>
            {nameLabel}
            {showMapped && (
              <ResponsiveStatusTag text="Mapped" currentScreenWidth={currentScreenWidth} />
            )}
            {isBlocked && (
              <ResponsiveStatusTag
                text="Blocked"
                type="blocked"
                currentScreenWidth={currentScreenWidth}
              />
            )}
          </div>
        );
      }
      return <span className={styles.nameCellText}>{cellData}</span>;
    },
    [selectedList, type, mappedEvents, activeFilter, blockedEventsName, currentScreenWidth],
  );

  const valueCellRenderer: ColumnShape['cellRenderer'] = ({ cellData }) => {
    return <span className={styles.valueCellText}>{humanize(cellData, '')}</span>;
  };

  // Columns https://autodesk.github.io/react-base-table/api/column
  const columns: ColumnShape[] = useMemo(() => {
    // How to get state in cell render
    // https://github.com/Autodesk/react-base-table#closure-problem-in-custom-renderers
    return [
      {
        title: 'Name',
        key: dimensionKey,
        dataKey: dimensionKey,
        width: '100%',
        align: Column.Alignment.LEFT,
        cellRenderer: nameCellRenderer,
        selectedList,
        mappedEvents,
        activeFilter,
        blockedEventsName,
        currentScreenWidth,
      },
      {
        title: 'Total Event',
        key: NUMBER_KEY,
        dataKey: NUMBER_KEY,
        width: 95,
        align: Column.Alignment.RIGHT,
        cellRenderer: valueCellRenderer,
      },
    ] as ColumnShape[];
  }, [
    selectedList,
    dimensionKey,
    mappedEvents,
    activeFilter,
    blockedEventsName,
    currentScreenWidth,
  ]);

  const rowEventHandlers = {
    onClick: ({ rowData }) => {
      const name = rowData[dimensionKey];
      if ((blockedEventsName ?? []).includes(name)) {
        return;
      }
      const value = rowData[NUMBER_KEY];
      if (name === activeRow?.name) {
        setActiveRow(null);
      } else {
        setActiveRow({
          dimension: dimensionKey,
          name,
          value,
        });
      }
      props.onRowClick?.({ dimension: dimensionKey, name, value });
    },
  };

  useEffect(() => {
    const dimensionName = dimensionKey;
    if (!dimensionName) return;
    setDimensionData([]);
    getDimensionData(
      { fields: [dimensionName], filters: props.filters || [] },
      {
        onSuccess: (data) => {
          setDimensionData(data?.data?.[0] ?? []);
        },
        onError: (err) => {
          setDimensionData([]);
        },
      },
    );
  }, [common.interval, common.appliedFilters, dimensionKey, props.filters]);

  useEffect(() => {
    // Handle table filter
    let tableData = dimensionData.filter((event) => {
      if (activeFilter === ETableFilter.Mapped) {
        return !!(mappedEvents?.data ?? []).find((item) => item?.value === event?.[dimensionKey]);
      } else if (activeFilter === ETableFilter.Unmapped) {
        return !(mappedEvents?.data ?? []).find((item) => item?.value === event?.[dimensionKey]);
      } else {
        return true;
      }
    });

    // Handle sorter order
    let key = NUMBER_KEY;
    if (sorterOrder.type === ESorterType.EVENT) {
      key = NUMBER_KEY;
    } else if (sorterOrder.type === ESorterType.NAME) {
      key = dimensionKey;
    }
    tableData.sort((prev, cur) => {
      if (sorterOrder.sorterOrder === ESorterOrder.ASC) {
        return prev[key] > cur[key] ? 1 : -1;
      }
      if (sorterOrder.sorterOrder === ESorterOrder.DESC) {
        return prev[key] > cur[key] ? -1 : 1;
      }
      return 0;
    });

    if (searchText) {
      tableData = tableData.filter((event) =>
        event?.[dimensionKey].toLowerCase().includes(searchText.toLowerCase()),
      );
    }

    setTableData(tableData);
  }, [dimensionData, mappedEvents, sorterOrder, activeFilter, dimensionKey, searchText]);

  useEffect(() => {
    props.onActiveRowChange?.(activeRow);
  }, [activeRow]);

  useEffect(() => {
    if (type === disshowDropdownDimensionType.mapped_event) {
      setDimensionKey(mappedEventDimensionType.all_event_name);
    } else {
      // @ts-ignore
      setDimensionKey(type);
    }
  }, [type]);

  return (
    <div className={styles.tableWrapper}>
      <DimensionTitle />
      <CustomTableHeader />
      {type === disshowDropdownDimensionType.mapped_event ? <CustomTableSubHeader /> : null}
      <div style={{ width: '100%', display: 'flex', flex: 1 }}>
        {isLoading ? (
          <Skeleton active={false} title={false} paragraph={{ rows: 10 }} />
        ) : (
          <BaseTable
            columns={columns}
            data={tableData}
            headerHeight={0}
            rowKey={dimensionKey}
            rowClassName={({ rowData }) => {
              return classNames(styles.tableRow, {
                [styles.tableRowActive]: rowData[dimensionKey] === activeRow?.name,
              });
            }}
            rowEventHandlers={rowEventHandlers}
          />
        )}
      </div>
    </div>
  );
};

export default DimensionTable;
